import React, { useLayoutEffect, useRef } from "react";
import { useBookingEditText } from "../text";
import SelectionButton from "./SelectionButton";
import { LONG_DISTANCE_FEE_TYPE } from "../constants";
import { formatLongDistanceFeeForInput, parseLongDistanceFeeInput } from "../utils";
import { cn } from "../../../../utils/classnames";
import { createTypographyHelper } from "../../../../utils/typography";

const countDigits = (text) => text.replace(/\D/g, "").length;

/** Index in the formatted text right after the given number of digits (commas are skipped over). */
const findCaretPosition = (formattedText, digitsBeforeCaret) => {
  let digitsSeen = 0;
  for (let index = 0; index < formattedText.length; index += 1) {
    if (digitsSeen === digitsBeforeCaret) return index;
    if (/\d/.test(formattedText[index])) digitsSeen += 1;
  }
  return formattedText.length;
};

/**
 * "Long Distance Fee" field in the Basic Information section.
 *
 * - Automatic: the server calculates the fee from the route distance.
 * - Manual:    the admin types the fee (JPY, charged per vehicle) and the
 *              server uses that amount instead of calculating it.
 */
const LongDistanceFeeField = ({ feeType, setFeeType, feeValue, setFeeValue }) => {
  const { t, language } = useBookingEditText();
  const getTypographyClass = createTypographyHelper(language);

  const isManual = feeType === LONG_DISTANCE_FEE_TYPE.MANUAL;

  const feeTypeOptions = [
    {
      value: LONG_DISTANCE_FEE_TYPE.AUTOMATIC,
      label: t("editBooking.BookingEditPage.basicInformation.longDistanceFeeOptions.automatic"),
    },
    {
      value: LONG_DISTANCE_FEE_TYPE.MANUAL,
      label: t("editBooking.BookingEditPage.basicInformation.longDistanceFeeOptions.manual"),
    },
  ];

  const feeInputRef = useRef(null);
  // How many digits sit before the caret. Adding/removing commas shifts the text,
  // so after each re-render we put the caret back after the same number of digits.
  const digitsBeforeCaretRef = useRef(null);

  const handleFeeInputChange = (event) => {
    const { value, selectionStart } = event.target;
    digitsBeforeCaretRef.current = countDigits(value.slice(0, selectionStart));
    setFeeValue(parseLongDistanceFeeInput(value));
  };

  useLayoutEffect(() => {
    const input = feeInputRef.current;
    const digitsBeforeCaret = digitsBeforeCaretRef.current;
    if (!input || digitsBeforeCaret === null) return;

    const caretPosition = findCaretPosition(input.value, digitsBeforeCaret);
    input.setSelectionRange(caretPosition, caretPosition);
    digitsBeforeCaretRef.current = null;
  }, [feeValue]);

  return (
    <div className="tw:flex tw:flex-col">
      <div className={`tw:mb-2 tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
        {t("editBooking.BookingEditPage.basicInformation.longDistanceFee")}
      </div>

      <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-2">
        <SelectionButton
          options={feeTypeOptions}
          value={feeType}
          onChange={setFeeType}
          fullWidth
          className={cn("tw:w-full", getTypographyClass(14))}
        />

        {/* The amount input only matters in manual mode, so hide it otherwise. */}
        {isManual && (
          <div className="tw:relative">
            <input
              ref={feeInputRef}
              id="manualLongDistanceFee"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="0"
              value={formatLongDistanceFeeForInput(feeValue)}
              onChange={handleFeeInputChange}
              aria-label={t("editBooking.BookingEditPage.basicInformation.longDistanceFee")}
              className={cn(
                "tw:h-[50px] tw:w-full tw:rounded-[5px] tw:!border tw:!border-[#e8e8e8] tw:bg-white tw:px-[14px] tw:pr-14",
                "tw:text-[15px] tw:font-normal tw:text-[#282828] tw:outline-none tw:focus:border-[#282828]"
              )}
            />
            <span className="tw:absolute tw:right-[14px] tw:top-1/2 tw:-translate-y-1/2 tw:text-[14px] tw:text-[#282828]">
              JPY
            </span>
          </div>
        )}
      </div>

      {isManual && (
        <p className={`tw:!mb-0 tw:!mt-1 tw:text-[#8d8d8d] ${getTypographyClass(12)}`}>
          {t("editBooking.BookingEditPage.basicInformation.longDistanceFeeManualHint")}
        </p>
      )}
    </div>
  );
};

export default LongDistanceFeeField;
